import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * useAudioPlayer — plays audio from blob URLs using HTML5 Audio API.
 *
 * Provides:
 *   playAudio(blobUrl, opts)  → Promise that resolves when playback ends
 *                                opts.loop:   if true, audio loops and promise resolves once playback starts
 *                                opts.volume: 0–1, default 1
 *   stopAudio()               → stops current playback
 *   isPlaying                 → boolean
 */
export default function useAudioPlayer() {
    const [isPlaying, setIsPlaying] = useState(false)
    const audioRef = useRef(null)
    const resolveRef = useRef(null)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.src = ''
                audioRef.current = null
            }
        }
    }, [])

    const stopAudio = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
        }
        setIsPlaying(false)
        if (resolveRef.current) {
            resolveRef.current()
            resolveRef.current = null
        }
    }, [])

    const playAudio = useCallback(
        (blobUrl, { loop = false, volume = 1 } = {}) => {
            return new Promise((resolve) => {
                // Stop any existing playback
                if (audioRef.current) {
                    audioRef.current.pause()
                    audioRef.current.src = ''
                }
                if (resolveRef.current) {
                    resolveRef.current()
                }

                if (!blobUrl) {
                    resolve()
                    return
                }

                const audio = new Audio(blobUrl)
                audio.loop = loop
                audio.volume = Math.max(0, Math.min(1, volume))
                audioRef.current = audio

                if (loop) {
                    audio.onerror = (err) => {
                        console.warn('[useAudioPlayer] Audio playback error:', err)
                        setIsPlaying(false)
                        resolve()
                    }

                    setIsPlaying(true)
                    audio.play()
                        .then(() => resolve())
                        .catch((err) => {
                            console.warn('[useAudioPlayer] Audio play() rejected:', err)
                            setIsPlaying(false)
                            resolve()
                        })
                    return
                }

                resolveRef.current = resolve

                audio.onended = () => {
                    setIsPlaying(false)
                    resolveRef.current = null
                    resolve()
                }

                audio.onerror = (err) => {
                    console.warn('[useAudioPlayer] Audio playback error:', err)
                    setIsPlaying(false)
                    resolveRef.current = null
                    resolve()
                }

                setIsPlaying(true)
                audio.play().catch((err) => {
                    console.warn('[useAudioPlayer] Audio play() rejected:', err)
                    setIsPlaying(false)
                    resolveRef.current = null
                    resolve()
                })
            })
        },
        []
    )

    return {
        playAudio,
        stopAudio,
        isPlaying,
    }
}
